// 月別控除入力シートのガソリンカード利用料を支払通知書へ反映する（本城さん依頼 #29）。
//
// 設計の要点:
//   - 「ガソリンカード貸与」フラグの有無に関わらず、月別控除入力シートに当月の実額があれば反映する。
//     （CLAUDE.md: フラグではなく実データで判定する。月別控除入力シートの運用説明＝
//      「当月の列に金額を入れると、その月の支払通知書に自動反映されます」に合わせる）
//   - 手入力（明細画面でガソリン単価を入れた）が既にあれば、それを優先してシート値で上書きしない。
//   - 当月のみ反映・繰り越しなし（月キーで突合）。

import type { Statement, ItemRow, SheetMaster } from "../../types";
import { finalizeStatement } from "./common";

const GAS_ITEM_NAME = "ガソリンカード利用料";

// 月別控除入力シート → 対象月のガソリン金額 lookup（管理番号と正規化氏名の両キーで引けるようにする）
export function buildGasLookup(sheetMaster: SheetMaster | null, month: string): Map<string, number> | null {
  if (!sheetMaster || sheetMaster.monthlyDeductions.length === 0) return null;
  const m = new Map<string, number>();
  for (const d of sheetMaster.monthlyDeductions) {
    if (d.month !== month) continue;
    if (!d.item.includes("ガソリン")) continue;
    if (d.empId) m.set(d.empId, d.amount);
    if (d.nameNormalized) m.set(d.nameNormalized, d.amount);
  }
  return m.size > 0 ? m : null;
}

// 月別控除入力シートのガソリン金額を通知書へ反映する。
//   1. 既に手入力済み（unitPrice が入っている）→ 手入力を優先し、何もしない
//   2. 貸与ありで手入力枠（unitPrice=null のガソリン行）がある → そこへ金額を流し込む
//   3. ガソリン行が無い（貸与フラグが空）→ 控除として新規行を追加して反映する
export function applyGasToStatement(stmt: Statement, amount: number): Statement {
  const gasRows = stmt.items.filter(
    (it) => it.category === "manual" && it.name === GAS_ITEM_NAME
  );
  // 1. 手入力済み（金額が入っている）なら手入力優先で触らない
  if (gasRows.some((it) => it.unitPrice != null)) return stmt;

  const signed = -Math.abs(amount);
  let items: ItemRow[];
  if (gasRows.length > 0) {
    // 2. 手入力枠（unitPrice=null）に流し込む
    items = stmt.items.map((it) =>
      it.category === "manual" && it.name === GAS_ITEM_NAME && it.unitPrice == null
        ? { ...it, unitPrice: signed, amount: signed * it.quantity, note: "月別控除入力シートから自動反映" }
        : it
    );
  } else {
    // 3. 貸与フラグが空でも、当月の実額があれば控除行を追加して反映する
    const row: ItemRow = {
      deliveryDate: stmt.salesDate,
      name: GAS_ITEM_NAME,
      unitPrice: signed,
      quantity: 1,
      amount: signed,
      taxRate: "non",
      category: "manual",
      note: "月別控除入力シートから自動反映（貸与フラグ未設定）",
    };
    items = [...stmt.items, row];
  }
  // 集計・値引き再導出は finalizeStatement に一元化（ガソリンは不課税なので値引き基準には入らない）
  return finalizeStatement({ ...stmt, items });
}
